import type { AccessRole } from '@gat-ship/types'

/**
 * Functional core — collection-level operations on `AccessRole[]`.
 *
 * Every function is pure: it takes the current list (read-only) and returns a
 * brand-new list (or a lookup result), never mutating the input and never
 * touching a ref, the network, or loading state. The imperative shell
 * (`useAccessRolesCollection`) owns the single write point that assigns these
 * results to the cache.
 */

/** Sort access roles by name (A→Z). Returns a new array; never mutates input. */
export function sortAccessRolesByName(
  roles: readonly AccessRole[]
): AccessRole[] {
  return [...roles].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Replace the role sharing `updated`'s id with a fresh copy. When no role
 * matches, the list is returned unchanged (a new array copy). Read-only.
 */
export function replaceAccessRoleById(
  roles: readonly AccessRole[],
  updated: AccessRole
): AccessRole[] {
  const index = roles.findIndex((role) => role.id === updated.id)
  if (index === -1) {
    return [...roles]
  }
  const next = [...roles]
  next.splice(index, 1, { ...updated })
  return next
}

/** Remove the role with `id`. No-op when absent. Read-only. */
export function removeAccessRoleById(
  roles: readonly AccessRole[],
  id: string
): AccessRole[] {
  return roles.filter((role) => role.id !== id)
}

/** Find a role by id. Read-only. */
export function findAccessRoleById(
  roles: readonly AccessRole[],
  id: string
): AccessRole | undefined {
  return roles.find((role) => role.id === id)
}

/** Whether a role with `id` exists in the list. Read-only. */
export function containsAccessRoleId(
  roles: readonly AccessRole[],
  id: string
): boolean {
  return roles.some((role) => role.id === id)
}
